/*
 * Treat — sticky add-to-cart bar (product page).
 *
 * Ported from Moonglaze's StickyAddToCart / StickyAddToCartContent (assets/sticky-add-to-cart.js),
 * rebuilt on the primitives Sense already ships rather than on Moonglaze's global event bus.
 *
 * Visibility: an IntersectionObserver on the real buy button, not a scroll handler. Moonglaze
 * compares scrollY to the product container's measured height on every scroll event, which has to
 * be recalculated whenever the layout moves. Watching the button itself states the intent directly
 * — show the substitute exactly when the original leaves the viewport — and costs nothing per
 * scroll frame.
 *
 * Variant sync: subscribes to PUB_SUB_EVENTS.variantChange. product-info.js does NOT re-render the
 * section on a variant change; it patches a fixed list of ids inside <product-info> and then
 * publishes. Anything outside that id list, which includes this bar, has to listen. Getting this
 * wrong would leave the bar quoting the price of a variant the customer already moved away from.
 */
if (!customElements.get('treat-sticky-add-to-cart')) {
  customElements.define(
    'treat-sticky-add-to-cart',
    class TreatStickyAddToCart extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);

        this.priceElement = this.querySelector('[data-treat-sticky-price]');
        this.optionsElement = this.querySelector('[data-treat-sticky-options]');
        this.imageElement = this.querySelector('[data-treat-sticky-image]');
        this.submitButton = this.querySelector('[data-treat-sticky-submit]');
        this.editButton = this.querySelector('[data-treat-sticky-edit]');

        this.onEditClick = this.onEditClick.bind(this);
        this.editButton?.addEventListener('click', this.onEditClick);

        this.watchBuyButton();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
      }

      disconnectedCallback() {
        this.editButton?.removeEventListener('click', this.onEditClick);
        this.observer?.disconnect();
        this.unsubscribe?.();
      }

      /*
       * The bar stands in for the real buy button, so the real buy button decides when it is
       * needed. Observed with no threshold, so it flips the moment the last pixel leaves.
       *
       * If the button cannot be found the bar stays hidden rather than showing permanently — a
       * bar pinned over the page for the whole visit is a worse failure than no bar.
       */
      watchBuyButton() {
        const buyButton =
          this.productInfo?.querySelector(`#ProductSubmitButton-${this.sectionId}`) ||
          this.productInfo?.querySelector('[type="submit"][name="add"]');

        if (!buyButton || !('IntersectionObserver' in window)) return;

        this.observer = new IntersectionObserver(
          ([entry]) => {
            // Only once the real button has scrolled ABOVE the viewport. On mobile the gallery comes
            // first, so "not intersecting" is also true at page load while the button is still below.
            this.classList.toggle(
              'treat-sticky-atc--visible',
              !entry.isIntersecting && entry.boundingClientRect.top < 0
            );
          },
          { rootMargin: '0px', threshold: 0 }
        );
        this.observer.observe(buyButton);
      }

      onVariantChange({ data }) {
        if (!data || data.sectionId !== this.sectionId) return;

        // No variant at all means the chosen combination does not exist. Sense calls this
        // "unavailable", which is not the same as sold out, so the button is disabled without
        // claiming a stock status the theme has not established.
        if (!data.variant) {
          this.setSubmitState(false, null);
          return;
        }

        this.syncPrice(data.html);
        this.syncOptions(data.variant);
        this.syncImage(data.variant);
        this.setSubmitState(data.variant.available, this.submitButton?.dataset.soldOutLabel);
      }

      // The price is taken from the markup product-info.js already fetched, not reformatted here.
      // Money formatting is a merchant setting rendered through treat-money in Liquid; rebuilding
      // it in JS would drift from the rest of the page the first time that setting changed.
      syncPrice(html) {
        if (!this.priceElement || !html) return;
        const source = html.getElementById(`price-${this.sectionId}`);
        if (source) this.priceElement.innerHTML = source.innerHTML;
      }

      // Taken from the variant object, not scraped out of the fetched markup. The picker emits
      // `{{ product.selected_or_first_available_variant | json }}` into [data-selected-variant]
      // (treat-product-variant-picker.liquid:164), so `options` is already the selected values in
      // option order — exact, and it cannot break when the picker's markup changes.
      syncOptions(variant) {
        if (!this.optionsElement || !Array.isArray(variant.options)) return;
        this.optionsElement.textContent = variant.options.join(' / ');
      }

      syncImage(variant) {
        if (!this.imageElement) return;
        const src = variant.featured_media?.preview_image?.src;
        if (!src) return;
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', '120');
        this.imageElement.setAttribute('src', url.href);
      }

      setSubmitState(isAvailable, unavailableLabel) {
        if (!this.submitButton) return;
        const addLabel = this.dataset.addLabel;
        const soldOutLabel = unavailableLabel || this.dataset.soldOutLabel;

        if (isAvailable) {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (addLabel) this.submitButton.textContent = addLabel;
        } else {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (soldOutLabel) this.submitButton.textContent = soldOutLabel;
        }
      }

      /*
       * Scroll back to the option picker and move focus there. Scrolling alone would strand a
       * keyboard or screen-reader user at the bottom of the page with the picker merely on screen,
       * so focus follows the scroll. tabindex is set only if the target cannot already take focus.
       */
      onEditClick(event) {
        event.preventDefault();
        // <variant-selects id="variant-selects-<sectionId>"> is the picker's real wrapper
        // (treat-product-variant-picker.liquid:53). The generic fallback covers a product whose
        // options render through some other path.
        const target =
          document.getElementById(`variant-selects-${this.sectionId}`) ||
          this.productInfo?.querySelector('variant-selects') ||
          this.productInfo?.querySelector('.product-form__input');

        if (!target) return;

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({
          behavior: prefersReducedMotion ? 'auto' : 'smooth',
          block: 'center',
        });

        const focusTarget = target.querySelector('input, select, button, [tabindex]') || target;
        if (focusTarget === target && !target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        focusTarget.focus({ preventScroll: true });
      }
    }
  );
}
